import React, { useEffect, useMemo } from 'react';
import { Grid, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import ModalLayout from 'commons/components/ModalLayout';
import PageLoading from 'commons/components/PageLoading';
import SelectCurrency from 'commons/components/SelectCurrency';
import FormulaResultsTable from 'screens/FormulaCalculation/components/FormulaResultsTable';
import formulaStyles from 'screens/FormulaCalculation/styles';
import useCurrency from 'utils/hooks/useCurrency';
import { getDollarKardex, getDollarZcot } from 'utils/currencyHelpers';
import { formatDateSlash } from 'utils/dateHelpers';

const useStyles = makeStyles(formulaStyles);

// Adapta la respuesta de GET /formulas/versions/:id (encabezado congelado + líneas) a la misma
// forma de `controls.materials`/`controls.totals` que ya consume FormulaResultsTable en vivo.
const buildTableControls = (detail) => {
  const lines = detail?.lines || [];
  const totalKardex = detail?.totalKardex || 0;
  const materials = lines.map((line) => ({
    component: line.component,
    name: line.name,
    subQuantity: line.subQuantity,
    base_unit: line.baseUnit,
    unit: line.unit,
    quant_base: line.quantBase,
    costDollar: line.kardexUnit,
    costPerFormulaDollar: line.kardex,
    zcot: line.zcotUnit,
    zcotPerFormula: line.zcotPerFormula,
    zcorUnit: line.zcorUnit,
    zcorPerFormula: line.zcorPerFormula,
    incidencePercent: totalKardex ? ((line.kardex || 0) / totalKardex) * 100 : 0,
  }));
  const totals = {
    totalQuantity: lines.reduce((acc, line) => acc + (line.subQuantity || 0), 0),
    totalCostDolar: totalKardex,
    totalZcot: lines.reduce((acc, line) => acc + (line.zcotPerFormula || 0), 0),
    totalZcor: detail?.totalZcor,
    totalVariableCosts: detail?.totalVariableCosts,
    kardexDolar: detail?.kardexDolar,
    totalProjectedCost: detail?.zcotProjectedCost,
    totalProjectedCostZcor: detail?.totalProjectedCost,
  };
  return { materials, totals };
};

const HistoricFormulaDetailModal = function (props) {
  const { open, toggleModalFnc, detail, loading, objects, quoteDetails } = props;
  const classes = useStyles();
  // Se convierte con los tipos de cambio guardados en la cotización, no con los vigentes en
  // Mantenedores, para que el detalle histórico en CLP no cambie si luego se actualizan.
  // Cotizaciones anteriores a guardar el dólar Kardex caen al valor vigente.
  const dollarKardex = quoteDetails?.kardexExchange || getDollarKardex(objects.currencies);
  const dollarZcot = quoteDetails?.zcotExchange || getDollarZcot(objects.currencies);
  const { currencyCode, setCurrencyCode, convertKardexValue, convertZcotValue } = useCurrency({
    dollarKardex,
    dollarZcot,
  });

  useEffect(() => {
    if (open) setCurrencyCode(quoteDetails?.quoteCurrencyCode === 'CLP' ? 'CLP' : 'USD');
  }, [open, quoteDetails?.quoteCurrencyCode]);

  const tableControls = useMemo(() => buildTableControls(detail), [detail]);

  return (
    <ModalLayout
      width="95%"
      maxWidth="1400px"
      minHeight="400px"
      open={open}
      toggleModalFnc={toggleModalFnc}>
      <Grid container mb="20px" justifyContent="space-between" alignItems="flex-start">
        <Grid item>
          <Typography variant="h4">
            {detail ? `${detail.materialId} - Detalle de fórmula` : 'Detalle de fórmula'}
          </Typography>
          {detail && (
            <Typography variant="body2" color="text.secondary">
              {detail.versionLabel
                ? `Versión ${detail.versionLabel} · ${formatDateSlash(detail.versionDate)}`
                : `ZCOT vigente al cotizar · ${formatDateSlash(detail.versionDate)}`}
            </Typography>
          )}
          {detail && currencyCode === 'CLP' && (
            <Typography variant="caption" color="text.secondary">
              {`Tipo de cambio de la cotización: USD Kardex ${Number(dollarKardex).toLocaleString(
                'es-CL',
              )} · USD ZCOT ${Number(dollarZcot).toLocaleString('es-CL')}`}
            </Typography>
          )}
        </Grid>
        <Grid item display="flex" alignItems="center" gap="12px">
          {detail && (
            <SelectCurrency currencyCode={currencyCode} setCurrencyCode={setCurrencyCode} />
          )}
          <IconButton onClick={toggleModalFnc} size="small">
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      {loading && <PageLoading />}
      {!loading && !detail && (
        <Typography color="text.secondary">
          No hay detalle de fórmula disponible para esta cotización.
        </Typography>
      )}
      {!loading && detail && (
        <FormulaResultsTable
          readOnly
          controls={tableControls}
          classes={classes}
          convertKardexValue={convertKardexValue}
          convertZcotValue={convertZcotValue}
          currencyCode={currencyCode}
        />
      )}
    </ModalLayout>
  );
};

export default HistoricFormulaDetailModal;
